import clsx from "clsx";

import { VideoToolTip } from "@components/video-tooltip";
import { useFeatureFlags } from "@hooks/http";
import { IconButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

import { Actions } from "./actions";
import {
  CustomMetricsAtomsProvider,
  useCustomOnConfirmModal,
  useHasSubmittedMetric,
  useSelectedIsDirtyMetric,
  useSelectedMetric,
} from "./atoms";
import { ClickOutConfirmCloseModal } from "./confirm-close-modal";
import { CustomMetricList } from "./custom-metric-list";
import { CustomMetricsLoading } from "./custom-metric-loading";
import { FormulaBuilder } from "./formula-builder";
import { FormulaControls } from "./formula-controls";
import { useCustomMetricsContext } from "./hooks/custom-metrics-provider";
import { Preview } from "./preview-table/preview";

const CustomMetricsModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [, setConfirmCloseModal] = useCustomOnConfirmModal();
  const [selectedMetric] = useSelectedMetric();
  const [isDirty] = useSelectedIsDirtyMetric();
  const [, setHasSubmitted] = useHasSubmittedMetric();

  return (
    <>
      <Modal
        size="3xl"
        isOpen={isOpen}
        onClose={() => {
          if ((selectedMetric && !isDirty) || !selectedMetric) {
            setHasSubmitted(false);
            onClose();
          } else {
            setConfirmCloseModal(true);
          }
        }}
        titleContent={
          <div className="flex items-center text-xl font-semibold">
            Custom metrics
            <VideoToolTip
              videoUrl="https://www.youtube.com/embed/UD4uKA-7RRk?si=zmLIjeObf6Oh3MUw"
              withoutHover
              contentDetails={{
                title: "Welcome to Custom Metrics!",
                content: (
                  <div>
                    This new feature allows you to create custom metrics built
                    off of Northbeam metrics as well as platform metrics.
                    <div className="mt-2">Watch the video to learn more.</div>
                  </div>
                ),
              }}
            />
          </div>
        }
        contentClassName="h-full max-h-[960px]"
        bodyClassName="h-[calc(100%-60px)]"
      >
        <div
          className="grid gap-4 w-full h-full overflow-none"
          style={{
            gridTemplateColumns:
              "minmax(160px, 0.4fr) minmax(400px, 800px) 0.6fr",
            gridTemplateRows: "auto 1fr 32px",
            gridTemplateAreas: `"custom-metric-list formula-input formula-controls"
                                "custom-metric-list preview formula-controls"
                                "custom-metric-list preview actions"`,
          }}
        >
          <CustomMetricList />
          <CustomMetricsLoading>
            <FormulaBuilder />
            <Preview />
            <FormulaControls />
            <Actions />
          </CustomMetricsLoading>
        </div>
      </Modal>
      <ClickOutConfirmCloseModal close={onClose} />
    </>
  );
};

export function CustomMetrics() {
  const { hasFeatureFlag } = useFeatureFlags();
  const { openModal } = useCustomMetricsContext();
  return (
    <div className="relative">
      <IconButton
        className="font-semibold leading-5 tracking-wide"
        variant="secondary"
        onClick={() => {
          openModal();
        }}
      >
        CUSTOM METRICS
      </IconButton>
      {hasFeatureFlag("customMetricsIsNew") && (
        <div
          className={clsx(
            "absolute -top-1.5 -right-1 ",
            "bg-pluto rounded-xl text-white px-2 h-4 text-xxs flex items-center justify-center",
          )}
        >
          NEW
        </div>
      )}
    </div>
  );
}

export function BareCustomMetrics({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <CustomMetricsAtomsProvider>
      <CustomMetricsModal isOpen={isOpen} onClose={onClose} />
    </CustomMetricsAtomsProvider>
  );
}

export function CustomMetricsNewBadge({ className }: { className?: string }) {
  const { hasFeatureFlag } = useFeatureFlags();

  if (hasFeatureFlag("customMetricsIsNew")) {
    return (
      <div
        className={clsx(
          "h-4 px-2 text-xxs rounded-xl bg-pluto text-white text-center flex items-center",
          className,
        )}
      >
        NEW
      </div>
    );
  }
  return null;
}
